<template>
  <EntityTemplate
    ref="content"
    v-if="currentExtension"
    :listed-entities="extensions"
    :entity="currentExtension"
    :data-is-dirty="extDataIsDirty || verDataIsDirty"
    :reload-fn="reload"
    :name="currentExtension.name"
    type="extension"
    :can-create="isAdmin"
    :can-duplicate="isAdmin"
    :can-delete="isAdmin"
    create-modal-component="NewExtensionModal"
    sidebar-title="Extensions"
    create-btn-label="Create extension"
    :open-attributes="openAttributes"
    @set-open-attributes="(v) => openAttributes = v"
    @undo="$store.commit('version/undoChanges')"
  >
    <template>
      <duplication-popup
        :open="showVersionDuplicationPopup"
        :name="versionDuplicateName"
        type="version"
        @update-popup="(v) => showVersionDuplicationPopup = v"
        @update-name="(v) => versionDuplicateName = v"
        @duplicate="duplicate"
      ></duplication-popup>

      <AttributesPanel
        title="Attributes"
        :data="currentExtension"
        :backup="extensionBackup"
        :attributes="[
          { name: 'name', title: 'Name', component: 'v-text-field', rules: [rules.required] },
          { name: 'description', title: 'Description', component: 'v-textarea', extra: { 'auto-grow': true, rows: 2 } },
          { name: 'categories', title: 'Categories', component: 'v-combobox', extra: {
            'multiple': true,
            'hide-selected': true,
            'persistent-hint': true,
            'hint': 'Press <Enter> to add a custom category',
            'small-chips': true,
            'items': defaultCategories,
          } },
        ]"
        @update="({ attr, value }) => $refs.content.updateAttribute(attr, value)"
      />
      <v-expansion-panel>
        <v-expansion-panel-header>Versions</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-text-field
            v-model="versionSearch"
            placeholder="Find version..."
            prepend-inner-icon="mdi-magnify"
            outlined
            clearable
            dense
            hide-details
            class="mb-4"
          ></v-text-field>

          <v-radio-group
            :value="currentExtension.defaultVersionUid"
            hide-details
            @change="setDefaultVersionUid"
          >
            <VersionDisplay
              v-for="version in filteredVersions"
              :key="version.uid"
              ref="version-display"
              :version="version"
              :used-version-names="getOtherVersionNames(version.uid)"
              @update="({ attr, value }) => updateVersionAttr(version.uid, attr, value)"
              @remove="removeVersion(version.uid)"
              @duplicate="tryDuplicateVersion(version.uid)"
              @add-env="addVersionEnvVar(version.uid)"
              @remove-env="(index) => removeVersionEnvVar(version.uid, index)"
              @update-env-key="({ oldKey, key, value }) => updateVersionEnvVarKey(version.uid, oldKey, key, value)"
              @update-env-value="({ key, value }) => updateVersionEnvVarValue(version.uid, key, value)"
            ></VersionDisplay>
          </v-radio-group>
          <v-row no-gutters align="center">
            <v-btn class="mr-2" icon outlined x-small :elevation="0" @click="addVersion">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
            <div>Add version</div>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </template>
  </EntityTemplate>
</template>

<script>
import { mapState } from 'vuex';
import cloneDeep from 'lodash.clonedeep';
import rules from '@/helpers/rules.js';
import { extension as extensionDefaults } from '@/helpers/models/defaults.js';

import EntityTemplate from '@/views/EntityTemplate.vue';
import AttributesPanel from '@/components/AttributesPanel.vue';
import VersionDisplay from '@/components/VersionDisplay.vue';

export default {
  name: 'Extension',
  components: {
    EntityTemplate,
    AttributesPanel,
    VersionDisplay
  },
  data() {
    return {
      openAttributes: [],
      rules,
      defaultCategories: extensionDefaults.categories,

      sidebarSearch: '',
      versionSearch: '',

      duplicatedVersionUid: null,
      showVersionDuplicationPopup: false,
      versionDuplicateName: '',
    };
  },
  computed: {
    ...mapState({
      extensions: (state) => state.extension.extensions,
      versions: (state) => state.version.versions,
      currentExtension: (state) => state.extension.currentExtension,
      extensionBackup: (state) => state.extension.extensionBackup,
      currentVersions: (state) => state.version.currentVersion, // /!\ no 's' because auto-generated
      extDataIsDirty: (state) => state.extension.dataIsDirty,
      verDataIsDirty: (state) => state.version.dataIsDirty,
    }),
    isAdmin() {
      return this.$store.getters['user/isAdmin']();
    },
    filteredExtensions() {
      if (!this.sidebarSearch || this.sidebarSearch.length < 2) return [];
      const search = this.sidebarSearch.toLowerCase();
      return this.extensions.filter((ext) => {
        return ext.name.toLowerCase().includes(search);
      });
    },
    filteredVersions() {
      if (!this.versionSearch) return this.currentVersions;
      const s = this.versionSearch;
      return this.currentVersions.filter((v) => {
        return v.name.toLowerCase().includes(s);
      });
    },
    allAttributesAreValid() {
      if (!this.attributesAreValid || !this.currentExtension) return false;
      for (const versionIndex in this.currentExtension.versions) {
        const child = this.$refs['version-display'][versionIndex];
        if (child && !child.checkAttributesAreValid()) return false;
      }
      return true;
    },
  },
  methods: {
    async load() {
      this.$store.commit('global/setInnerLoading', { innerLoading: true });
      await this.prepareData();
    },
    async reload() {
      await this.load();
    },
    async prepareData() {
      if (this.extensions.length === 0)
        await this.$store.dispatch('extension/getExtensions');
      if (this.versions.length === 0)
        await this.$store.dispatch('version/getVersions');

      let ext = this.extensions.find((e) => e.uid === this.$route.params.id);
      this.$store.commit('extension/setCurrent', { currentExtension: ext });
      this.$store.commit('version/setCurrent', { currentVersion: ext.versions.map((v) => v.item) });
      this.openAttributes = [
        0, // attributes
        1, // env vars
      ];

      const $this = this;
      this.$nextTick(() => {
        $this.$refs.content.setRelationData({
          Project: {
            title: 'Used in projects',
            // modifiable: false,
          },
        });
      });
      this.$store.commit('global/setInnerLoading', { innerLoading: false });
    },
    getOtherVersionNames(version) {
      if (!this.extensionBackup) return [];
      return this.extensionBackup.versions.filter((v) => v.uid !== version).map((v) => v.name);
    },
    addVersion() {
      this.$store.commit('version/addVersionToCurrent');
    },
    removeVersion(version) {
      this.$store.commit('version/removeVersionFromCurrent', { version });
    },
    addVersionEnvVar(version) {
      this.$store.commit('version/addEnvVar', { version });
    },
    removeVersionEnvVar(version, index) {
      this.$store.commit('version/removeEnvVar', { version, index });
    },
    updateVersionAttr(version, attr, value) {
      this.$store.commit('version/updateAttr', { version, attr, value });
    },
    updateVersionEnvVarKey(version, oldKey, key, value) {
      this.$store.commit('version/updateEnvVarKey', { version, oldKey, key, value });
    },
    updateVersionEnvVarValue(version, key, value) {
      this.$store.commit('version/updateEnvVarValue', { version, key, value });
    },
    tryDuplicateVersion(uid) {
      this.versionDuplicateName = `${this.versions.find((v) => v.uid === uid).name}_copy`;
      this.duplicatedVersionUid = uid;
      this.showVersionDuplicationPopup = true;
    },
    setDefaultVersionUid(v) {
      if (!v) return;
      const uid = this.currentExtension.uid;
      this.$store.commit('extension/updateExtension', { uid, key: 'defaultVersionUid', value: v });
      this.$store.commit('extension/updateCurrent', { key: 'defaultVersionUid', value: v });
    },
    duplicate() {
      let origin = cloneDeep(this.versions.find((v) => v.uid === this.duplicatedVersionUid));
      origin.name = this.versionDuplicateName;
      this.$store.commit('version/addVersionToCurrent', { origin });
      this.showVersionDuplicationPopup = false;
    },
  },
  created() {
    this.load();
  },

  beforeRouteUpdate(to, from, next) {
    this.$refs.content.checkPageLeave(to, from, next);
  },
  beforeRouteLeave(to, from, next) {
    this.$refs.content.checkPageLeave(to, from, next);
  },
};
</script>

<style scoped>
.noextension-placeholder {
  display: flex;
  justify-content: center;
}
</style>
